/** Rôle tel que le back le nomme sur un lien personnel de régie. */
export type StaffLinkRole = 'PRESENTER' | 'MODERATOR';

/** Vocabulaire du clientId Ably, seul rôle que la régie sait interpréter. */
export type AdminChatRole = 'admin' | 'moderator';

export interface StaffAccessLinkResolution {
  workspaceId: number;
  webinarId: number;
  sessionId: number;
  role: StaffLinkRole;
}

export type StaffAccessLinkFailure =
  | { kind: 'wrongAccount'; expectedEmail: string | null }
  | { kind: 'accessDenied' }
  | { kind: 'notFound' }
  | { kind: 'unknown' };

const WRONG_ACCOUNT_KEY = 'HTTP.ERROR.403.ADMIN.LIVE.LINK.WRONG.ACCOUNT';
const ACCESS_DENIED_KEY = 'HTTP.ERROR.403.ADMIN.LIVE.LINK.ACCESS.DENIED';
const LINK_NOT_FOUND_KEY = 'HTTP.ERROR.404.ADMIN.LIVE.LINK.NOT.FOUND';
const SESSION_NOT_FOUND_KEY = 'HTTP.ERROR.404.WEBINAR.SESSION.NOT.FOUND';
const EXPECTED_ACCOUNT_FIELD = 'expectedAccountEmail';

export function toAdminChatRole(role: StaffLinkRole | null | undefined): AdminChatRole | null {
  if (role === 'PRESENTER') {
    return 'admin';
  }
  if (role === 'MODERATOR') {
    return 'moderator';
  }
  return null;
}

/** Le back renvoie l'e-mail masqué dans `errors[0]`, jamais l'adresse complète. */
export function extractExpectedAccountEmail(details: unknown): string | null {
  if (!Array.isArray(details)) {
    return null;
  }
  for (const entry of details) {
    if (typeof entry !== 'object' || entry === null) {
      continue;
    }
    const validationError = entry as { message?: unknown; fieldName?: unknown };
    if (validationError.fieldName === EXPECTED_ACCOUNT_FIELD) {
      return typeof validationError.message === 'string' && validationError.message.length > 0
        ? validationError.message
        : null;
    }
  }
  return null;
}

export function classifyStaffAccessLinkError(error: unknown): StaffAccessLinkFailure {
  if (typeof error !== 'object' || error === null) {
    return { kind: 'unknown' };
  }
  const appError = error as { message?: unknown; status?: unknown; details?: unknown };
  const message = typeof appError.message === 'string' ? appError.message : '';
  const status = typeof appError.status === 'number' ? appError.status : null;

  if (message === WRONG_ACCOUNT_KEY) {
    return { kind: 'wrongAccount', expectedEmail: extractExpectedAccountEmail(appError.details) };
  }
  if (message === ACCESS_DENIED_KEY) {
    return { kind: 'accessDenied' };
  }
  if (message === LINK_NOT_FOUND_KEY || message === SESSION_NOT_FOUND_KEY) {
    return { kind: 'notFound' };
  }
  // Un lien révoqué ou inconnu peut remonter sans clé exploitable : le statut suffit à trancher.
  if (status === 404) {
    return { kind: 'notFound' };
  }
  if (status === 403) {
    return { kind: 'accessDenied' };
  }
  return { kind: 'unknown' };
}

/**
 * Une régie n'est identifiée que par le triplet complet : la route est la même d'un lien à l'autre,
 * donc `AblyAuth` n'est pas remonté et l'état de la cible précédente survit à la navigation.
 */
export function staffLinkTargetKey(
  webinarId: number | null,
  sessionId: number | null,
  staffLinkId: string | null,
): string | null {
  if (!webinarId || !sessionId || !staffLinkId) {
    return null;
  }
  return `${webinarId}:${sessionId}:${staffLinkId}`;
}

/** Résolution mémorisée, indissociable de la cible pour laquelle elle a été demandée. */
export interface StaffLinkState {
  key: string;
  resolution: StaffAccessLinkResolution | null;
  failure: StaffAccessLinkFailure | null;
}

export type StaffLinkGate =
  | { status: 'absent' }
  | { status: 'pending' }
  | { status: 'failed'; failure: StaffAccessLinkFailure }
  | { status: 'resolved'; resolution: StaffAccessLinkResolution };

/**
 * Un état portant une autre clé appartient au lien précédent : il vaut « en attente », jamais
 * « prêt », sinon la régie de A servirait de réponse à B.
 */
export function staffLinkGate(
  targetKey: string | null,
  state: StaffLinkState | null,
): StaffLinkGate {
  if (!targetKey) {
    return { status: 'absent' };
  }
  if (!state || state.key !== targetKey) {
    return { status: 'pending' };
  }
  if (state.failure) {
    return { status: 'failed', failure: state.failure };
  }
  if (state.resolution) {
    return { status: 'resolved', resolution: state.resolution };
  }
  return { status: 'pending' };
}

/**
 * La bascule ne se raconte que si elle quitte un espace réellement choisi. Après une connexion,
 * l'espace actif n'est que le premier de la liste : annoncer un départ depuis lui serait faux.
 */
export function shouldAnnounceWorkspaceSwitch({
  previousWorkspaceId,
  targetWorkspaceId,
  storedWorkspaceId,
}: {
  previousWorkspaceId: number | null | undefined;
  targetWorkspaceId: number;
  storedWorkspaceId: number | null;
}): boolean {
  if (previousWorkspaceId == null || previousWorkspaceId === targetWorkspaceId) {
    return false;
  }
  return storedWorkspaceId === previousWorkspaceId;
}

/** Clé i18n du message affiché à la place de la régie quand le lien ne s'ouvre pas. */
export function staffAccessLinkErrorKey(failure: StaffAccessLinkFailure): string {
  switch (failure.kind) {
    case 'wrongAccount':
      // Le back peut masquer l'e-mail sans le fournir : on ne promet pas une adresse qu'on n'a pas.
      return failure.expectedEmail ? 'staffLink.wrongAccount' : 'staffLink.wrongAccountUnknown';
    case 'accessDenied':
      return 'staffLink.accessDenied';
    case 'notFound':
      return 'staffLink.notFound';
    default:
      return 'staffLink.unknown';
  }
}
